import { frontendURL } from 'dashboard/helper/URLHelper';
import {
  buildOfferMessage as buildContextualOfferMessage,
  getOfferDeepLink,
} from 'dashboard/helper/lais/offerPresenter';
import { buildSuggestHouseMessage } from 'dashboard/helper/lais/imovelPresenter';

export const AGENDA_QUICK_ACTIONS = {
  INTRODUCTION: 'introduction',
  OFFER: 'offer',
  CONFIRM: 'confirm',
  OWNER: 'owner',
  SUGGEST_HOUSE: 'suggest_house',
};

export const AGENDA_AGENT_SUPPORT_STATUS = Object.freeze({
  COMPLETE: 'COMPLETE',
  IN_PROGRESS: 'IN_PROGRESS',
  PENDING: 'PENDING',
  TRANSFERRED: 'TRANSFERRED',
});

const toText = value => String(value || '').trim();

export const contactName = visit =>
  toText(visit?.contact?.name || visit?.contactName) || 'cliente';

const firstName = value => toText(value).split(/\s+/)[0] || 'cliente';

export const contactPhone = visit =>
  toText(
    visit?.contact?.phoneNumber ||
      visit?.contact?.phone_number ||
      visit?.contactPhone ||
      visit?.phoneNumber
  );

export const ownerPhone = visit =>
  toText(
    visit?.ownerContact?.phoneNumber ||
      visit?.ownerContact?.phone_number ||
      visit?.owner_contact?.phoneNumber ||
      visit?.owner_contact?.phone_number ||
      visit?.imovel?.ownerContact?.phoneNumber ||
      visit?.imovel?.ownerContact?.phone_number ||
      visit?.imovel?.owner_contact?.phoneNumber ||
      visit?.imovel?.owner_contact?.phone_number ||
      visit?.imovel?.ownerPhone ||
      visit?.imovel?.owner_phone ||
      visit?.additionalAttributes?.ownerPhone ||
      visit?.additionalAttributes?.owner_phone ||
      visit?.additionalAttributes?.responsiblePhone ||
      visit?.additionalAttributes?.responsible_phone
  );

const visitAttributes = visit =>
  visit?.additionalAttributes || visit?.additional_attributes || {};

const agentSupportPayload = visit => {
  const attrs = visitAttributes(visit);
  return (
    visit?.agentSupport ||
    visit?.agent_support ||
    attrs.agentSupport ||
    attrs.agent_support ||
    attrs.agentSupportStatus ||
    attrs.agent_support_status ||
    null
  );
};

export const agentSupportStatus = visit => {
  const payload = agentSupportPayload(visit);
  const attrs = visitAttributes(visit);
  let payloadStatus = null;
  if (payload && typeof payload === 'object') {
    payloadStatus = payload.status || payload.state || payload.current_status;
  } else if (typeof payload === 'string') {
    payloadStatus = payload;
  }

  const rawStatus =
    payloadStatus ||
    attrs.agentSupportStatus ||
    attrs.agent_support_status ||
    visit?.agentSupportStatus ||
    visit?.agent_support_status;
  const status = toText(rawStatus).toUpperCase();

  return Object.values(AGENDA_AGENT_SUPPORT_STATUS).includes(status)
    ? status
    : '';
};

export const agentSupportId = visit => {
  const payload = agentSupportPayload(visit);
  const attrs = visitAttributes(visit);
  if (payload && typeof payload === 'object') {
    return toText(
      payload.supportId ||
        payload.support_id ||
        payload.id ||
        payload.identifier ||
        payload.ticketId ||
        payload.ticket_id
    );
  }

  return toText(
    attrs.agentSupportId ||
      attrs.agent_support_id ||
      attrs.supportId ||
      attrs.support_id ||
      visit?.agentSupportId ||
      visit?.agent_support_id
  );
};

export const isAgentSupportPending = visit =>
  agentSupportStatus(visit) === AGENDA_AGENT_SUPPORT_STATUS.PENDING;

export const isAgentSupportInProgress = visit =>
  agentSupportStatus(visit) === AGENDA_AGENT_SUPPORT_STATUS.IN_PROGRESS;

export const propertyAddress = visit =>
  [
    visit?.imovel?.titulo || visit?.imovel?.endereco || visit?.propertyTitle,
    visit?.imovel?.bairro,
    visit?.imovel?.cidade,
  ]
    .filter(Boolean)
    .map(toText)
    .join(', ');

export const propertyId = visit => visit?.imovel?.id || visit?.imovelId;

export const normalizedWhatsappPhone = phone => {
  const digits = toText(phone).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('55')) return digits;
  if (digits.length === 10 || digits.length === 11) return `55${digits}`;
  return digits;
};

export const formatVisitDateForMessage = value => {
  if (!value) return 'data a combinar';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'data a combinar';

  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatVisitTimeForMessage = value => {
  if (!value) return 'horário combinado';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'horário combinado';

  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const getDayOffset = (value, now = new Date()) => {
  const date = new Date(value);
  const current = new Date(now);
  if (Number.isNaN(date.getTime()) || Number.isNaN(current.getTime())) {
    return 'no dia combinado';
  }

  const startOf = item =>
    new Date(item.getFullYear(), item.getMonth(), item.getDate()).getTime();
  const diffDays = Math.round(
    (startOf(date) - startOf(current)) / (24 * 60 * 60 * 1000)
  );

  if (diffDays === 0) return 'hoje';
  if (diffDays === 1) return 'amanhã';

  return new Intl.DateTimeFormat('pt-BR', { weekday: 'long' }).format(date);
};

export const buildWhatsAppUrl = ({ phone, message }) => {
  const normalizedPhone = normalizedWhatsappPhone(phone);
  if (!normalizedPhone) return '';

  return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(
    toText(message)
  )}`;
};

export const proposalWorkspaceUrl = ({ accountId, visit, origin = '' }) => {
  const params = new URLSearchParams();
  const imovelId = propertyId(visit);
  const currentContactId = visit?.contact?.id || visit?.contactId;

  if (imovelId) params.set('imovel_id', imovelId);
  if (currentContactId) params.set('contact_id', currentContactId);
  params.set('new', '1');

  const query = params.toString();
  const path = frontendURL(`accounts/${accountId}/propostas`);
  return `${origin}${path}${query ? `?${query}` : ''}`;
};

export const hasVisitCheckIn = visit =>
  Boolean(
    visit?.checkInStatus === 'confirmed' ||
      visit?.check_in_status === 'confirmed' ||
      visit?.visitor?.checkInStatus === 'confirmed' ||
      visit?.visitor?.check_in_status === 'confirmed' ||
      visit?.additionalAttributes?.checkInDone ||
      visit?.additional_attributes?.check_in_done
  );

export const buildIntroductionMessage = ({
  visit,
  agentName = 'Imudei',
  companyName = 'Imudei',
  now = new Date(),
}) => {
  const name = firstName(contactName(visit));
  const dayText = getDayOffset(visit?.scheduledAt, now);
  const timeText = formatVisitTimeForMessage(visit?.scheduledAt);
  const checkInDone = hasVisitCheckIn(visit);
  const street =
    toText(visit?.imovel?.endereco || visit?.imovel?.titulo) ||
    'endereço combinado';
  const houseNumber =
    toText(
      visit?.imovel?.numero ||
        visit?.imovel?.number ||
        visit?.additionalAttributes?.number ||
        visit?.additional_attributes?.number
    ) || 's/n';
  const region =
    [visit?.imovel?.bairro, visit?.imovel?.cidade].filter(Boolean).join(', ') ||
    'região informada';
  const baseMessage =
    `Oi ${name}. Sou ${agentName} e trabalho no time dos corretores parceiros do ${companyName}. ` +
    `Vou te acompanhar na visita ${dayText} às ${timeText}.`;
  const meetingMessage =
    `Vamos nos encontrar em frente ao imóvel na ${street}, ${houseNumber} - ${region}, ok? ` +
    'Qualquer dúvida, é só chamar!';

  return checkInDone
    ? `${baseMessage} Obrigado por ter feito a confirmação da sua visita! ${meetingMessage}`
    : `${baseMessage} ${meetingMessage}`;
};

export const buildConfirmVisitMessage = ({ visit }) => {
  const name = firstName(contactName(visit));
  const timeText = formatVisitTimeForMessage(visit?.scheduledAt);
  const houseAddress =
    toText(visit?.imovel?.endereco || visit?.imovel?.titulo) ||
    'endereço combinado';
  const houseNumber =
    toText(
      visit?.imovel?.numero ||
        visit?.imovel?.number ||
        visit?.additionalAttributes?.number ||
        visit?.additional_attributes?.number
    ) || 's/n';

  return (
    `Olá, ${name}! Hoje às ${timeText} vou te acompanhar na visita no imóvel na ` +
    `${houseAddress}, ${houseNumber}. Te encontro na frente do imóvel, ok? ` +
    'Se precisar de qualquer ajuda, é só chamar!'
  );
};

export const buildOfferMessage = ({ accountId, visit, origin = '' }) => {
  const name = contactName(visit);
  const address = propertyAddress(visit) || 'imóvel visitado';
  const imovelId = propertyId(visit);
  const contactId = visit?.contact?.id || visit?.contactId;

  return buildContextualOfferMessage({
    accountId,
    contactName: name,
    address,
    businessContext:
      visit?.businessContext ||
      visit?.business_context ||
      visit?.imovel?.businessContext ||
      visit?.imovel?.business_context ||
      visit?.imovel?.finalidade,
    imovelId,
    contactId,
    origin,
  });
};

export const offerDeepLink = ({ accountId, visit, origin = '' }) =>
  getOfferDeepLink({
    accountId,
    imovelId: propertyId(visit),
    contactId: visit?.contact?.id || visit?.contactId,
    businessContext:
      visit?.businessContext ||
      visit?.business_context ||
      visit?.imovel?.businessContext ||
      visit?.imovel?.business_context ||
      visit?.imovel?.finalidade,
    origin,
  });

export const buildVisitSuggestHouseMessage = ({
  accountId,
  visit,
  origin = '',
}) =>
  buildSuggestHouseMessage({
    accountId,
    contact: visit?.contact || { name: contactName(visit) },
    imovel: visit?.imovel,
    businessContext:
      visit?.businessContext ||
      visit?.business_context ||
      visit?.imovel?.businessContext ||
      visit?.imovel?.business_context ||
      visit?.imovel?.finalidade,
    origin,
  });

export const actionRecord = (visit, action) => {
  const attrs = visit?.additionalAttributes || {};
  const records = attrs.agendaQuickActions || attrs.agenda_quick_actions || {};
  return records[action] || null;
};

export const actionCompleted = (visit, action) =>
  !!actionRecord(visit, action)?.completed_at ||
  !!actionRecord(visit, action)?.completedAt;

export const quickActionAttributes = ({ action, extra = {} }) => ({
  agenda_quick_actions: {
    [action]: {
      completed_at: new Date().toISOString(),
      ...extra,
    },
  },
});

export const canSendIntroduction = visit => !!propertyAddress(visit);

export const canSendOffer = visit =>
  !!contactName(visit) &&
  !!contactPhone(visit) &&
  !!propertyId(visit) &&
  !!propertyAddress(visit);

export const ownerContactRouteIds = visit => {
  const owner =
    visit?.ownerContact ||
    visit?.owner_contact ||
    visit?.imovel?.ownerContact ||
    visit?.imovel?.owner_contact ||
    {};
  const attrs =
    visit?.additionalAttributes || visit?.additional_attributes || {};

  return {
    contactId:
      owner.contactId || owner.contact_id || owner.id || attrs.ownerContactId,
    inboxId: owner.inboxId || owner.inbox_id || attrs.ownerInboxId,
    conversationId:
      owner.conversationId ||
      owner.conversation_id ||
      attrs.ownerConversationId,
  };
};

export const canTalkToOwner = visit => {
  const ids = ownerContactRouteIds(visit);
  return (
    !!ownerPhone(visit) ||
    !!ids.conversationId ||
    !!(ids.contactId && ids.inboxId)
  );
};

export const canSuggestHouse = visit =>
  !!contactName(visit) && !!contactPhone(visit) && !!propertyId(visit);
